import { useLogto } from '@logto/react';
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  createApiClient,
  type AvailabilitySlotView,
  type Booking,
  type ChannelSplit,
  type CustomerListItem,
  type Tour
} from '../api/client';
import { filterCustomers } from '../customers';
import { ErrorBanner } from '../ui/ErrorBanner';
import { channelLabel, formatDateTime, formatEur } from '../ui/format';

const CHANNELS = ['WalkIn', 'Ota', 'OwnSite'] as const;

export function Book() {
  const { getAccessToken } = useLogto();
  const api = useMemo(() => createApiClient(getAccessToken), [getAccessToken]);
  const [tours, setTours] = useState<Tour[]>([]);
  const [tourId, setTourId] = useState('');
  const [slots, setSlots] = useState<AvailabilitySlotView[]>([]);
  const [startsAt, setStartsAt] = useState('');
  const [channel, setChannel] = useState<string>('OwnSite');
  const [pax, setPax] = useState('2');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [customerQuery, setCustomerQuery] = useState('');
  const [picked, setPicked] = useState<CustomerListItem | null>(null);
  const [quote, setQuote] = useState<ChannelSplit | null>(null);
  const [recent, setRecent] = useState<Booking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const loadTours = useCallback(async () => {
    try {
      const ts = await api.getTours();
      setTours(ts);
      setTourId((prev) => (prev === '' && ts[0] ? ts[0].id : prev));
    } catch (err) {
      setError((err as Error).message);
    }
  }, [api]);

  const loadRecent = useCallback(async () => {
    try {
      setRecent(await api.getBookings());
    } catch {
      // the recent list is best-effort
    }
  }, [api]);

  const loadCustomers = useCallback(async () => {
    try {
      setCustomers(await api.getCustomers());
    } catch {
      // the picker is best-effort; a new customer can still be typed
    }
  }, [api]);

  useEffect(() => {
    void loadTours();
    void loadRecent();
    void loadCustomers();
  }, [loadTours, loadRecent, loadCustomers]);

  useEffect(() => {
    if (!tourId) {
      setSlots([]);
      return;
    }
    api
      .getAvailability(tourId)
      .then((s) => {
        setSlots(s);
        setStartsAt((prev) => prev || s.find((x) => x.remaining > 0)?.startsAt || '');
      })
      .catch(() => setSlots([]));
  }, [tourId, api]);

  // Pricing is server-side only: the quote is fetched, never computed in the browser.
  useEffect(() => {
    if (!tourId) {
      setQuote(null);
      return;
    }
    let active = true;
    api
      .getQuote(tourId, channel)
      .then((q) => {
        if (active) setQuote(q);
      })
      .catch(() => {
        if (active) setQuote(null);
      });
    return () => {
      active = false;
    };
  }, [tourId, channel, api]);

  const paxN = Math.max(1, Number(pax) || 1);
  const matches = customerQuery.trim() === '' ? [] : filterCustomers(customers, customerQuery).slice(0, 6);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(null);
    try {
      await api.createBooking({
        tourId,
        startsAt,
        channel,
        pax: paxN,
        customerName: picked ? null : name || null,
        customerEmail: picked ? null : email || null,
        customerPhone: picked ? null : phone || null,
        customerId: picked ? picked.id : null,
        language: 'pt'
      });
      setSaved('Reserva criada. A confirmação é enviada ao cliente (no ambiente local vai para o log da API).');
      setName('');
      setEmail('');
      setPhone('');
      setPicked(null);
      setCustomerQuery('');
      await loadRecent();
      await loadCustomers();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="page">
      <header className="page-head">
        <h1>Reservar</h1>
        <p className="subtle">Registe uma reserva e veja já quanto o operador recebe.</p>
      </header>
      <ErrorBanner message={error} />
      {saved ? <div className="alert-ok" role="status">{saved}</div> : null}
      <div className="two-col">
        <form className="card" onSubmit={submit}>
          <label className="field">
            <span>Tour</span>
            <select value={tourId} onChange={(e) => setTourId(e.target.value)} required>
              <option value="" disabled>escolher…</option>
              {tours.map((t) => (
                <option key={t.id} value={t.id}>{t.title} ({formatEur(t.basePriceEur)})</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Horário</span>
            <select value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required>
              <option value="" disabled>escolher…</option>
              {slots.map((s) => (
                <option key={s.startsAt} value={s.startsAt} disabled={s.remaining <= 0}>
                  {formatDateTime(s.startsAt)} — {s.remaining}/{s.capacity} livres
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Canal</span>
            <select value={channel} onChange={(e) => setChannel(e.target.value)}>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>{channelLabel(c)}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Pax</span>
            <input type="number" min="1" value={pax} onChange={(e) => setPax(e.target.value)} required />
          </label>

          <div className="field">
            <span>Cliente</span>
            {picked ? (
              <div className="picked">
                <span>{picked.displayName}</span>
                <button type="button" className="btn btn-ghost" onClick={() => setPicked(null)}>Mudar</button>
              </div>
            ) : (
              <>
                <input
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                  placeholder="Procurar cliente existente…"
                  aria-label="Procurar cliente existente"
                />
                {matches.length > 0 ? (
                  <ul className="picker">
                    {matches.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => {
                            setPicked(c);
                            setCustomerQuery('');
                          }}
                        >
                          {c.displayName}{c.identities[0] ? ` · ${c.identities[0].value}` : ''}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}
          </div>

          {picked ? null : (
            <>
              <label className="field">
                <span>Nome do cliente</span>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="novo cliente (opcional)" />
              </label>
              <label className="field">
                <span>Email</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="opcional" />
              </label>
              <label className="field">
                <span>Telefone (WhatsApp)</span>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="opcional" />
              </label>
            </>
          )}

          <button className="btn btn-primary" type="submit" disabled={!tourId || !startsAt}>Reservar</button>
        </form>

        <aside className="card">
          <h2>Quanto recebe</h2>
          {quote ? (
            <dl className="quote">
              <div>
                <dt>Cliente paga</dt>
                <dd className="money">{formatEur(quote.customerPays * paxN)}</dd>
              </div>
              <div className="accent">
                <dt>Operador recebe</dt>
                <dd className="money">{formatEur(quote.operatorNet * paxN)}</dd>
              </div>
              <div>
                <dt>Plataforma</dt>
                <dd className="money">{formatEur(quote.platformNet * paxN)}</dd>
              </div>
              {quote.otaCommission > 0 ? (
                <div>
                  <dt>OTA</dt>
                  <dd className="money">{formatEur(quote.otaCommission * paxN)}</dd>
                </div>
              ) : null}
              <div className="per-seat">
                <dt>Operador por lugar</dt>
                <dd className="money">{formatEur(quote.operatorNet)}</dd>
              </div>
            </dl>
          ) : (
            <p className="subtle">Escolha um tour e um canal.</p>
          )}
        </aside>
      </div>

      <section className="card">
        <h2>Reservas recentes</h2>
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Cliente</th>
                <th>Canal</th>
                <th className="num">Pax</th>
                <th className="num">Operador</th>
              </tr>
            </thead>
            <tbody>
              {recent.slice(0, 10).map((b) => (
                <tr key={b.id}>
                  <td>{formatDateTime(b.startsAt)}</td>
                  <td>{b.customer?.displayName ?? b.customerName ?? '—'}</td>
                  <td>{channelLabel(b.channel)}</td>
                  <td className="num">{b.pax}</td>
                  <td className="num money">{formatEur(b.operatorNetEur)}</td>
                </tr>
              ))}
              {recent.length === 0 ? (
                <tr>
                  <td colSpan={5} className="subtle">Sem reservas ainda.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
